"use client"

import { ReactNode, useEffect, useState } from "react"
import { StorageIntegration } from "@/schema"
import {
  isRecordingActive,
  recordingActiveInAnyTab,
  useMeetingRecordingStore,
} from "@/stores/use-meeting-recording-store"
import { DirectUploadError } from "@/utils/direct-upload"
import { useAuth } from "@clerk/nextjs"
import * as Sentry from "@sentry/nextjs"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { persistQueryClient } from "@tanstack/react-query-persist-client"
import { toast } from "sonner"

import { ApiRequestError } from "@/hooks/api/base"
import { storageIntegrationsQueryKeys } from "@/hooks/api/storage_integrations/queries"

const GLOBAL_ERROR_FALLBACK = "Something went wrong"

const STORAGE_INVALID_AUTH_ERROR = "storage_integration_invalid_auth"

const INACTIVITY_LOGOUT_ERROR = "session_expired_inactivity"
const RECORDING_EXPIRY_TOAST_THROTTLE_MS = 5 * 60_000
let inactivityLogoutTriggered = false
let inactivityRecordingWarnedAt = 0

function handleInactivityLogout(error: unknown) {
  if (!(error instanceof ApiRequestError) || error.message !== INACTIVITY_LOGOUT_ERROR) return false
  if (isRecordingActive(useMeetingRecordingStore.getState()) || recordingActiveInAnyTab()) {
    if (Date.now() - inactivityRecordingWarnedAt >= RECORDING_EXPIRY_TOAST_THROTTLE_MS) {
      inactivityRecordingWarnedAt = Date.now()
      toast.error(
        "Your session expired during a recording. Audio is being saved locally — sign in again to finish uploading."
      )
    }
    return true
  }
  if (inactivityLogoutTriggered || typeof window === "undefined") return true
  inactivityLogoutTriggered = true
  window.location.assign("/logout")
  return true
}

function invalidStorageIntegrationId(error: ApiRequestError): string | undefined {
  const body = error.responseBody
  if (typeof body !== "object" || body === null || !("storage_integration_id" in body)) return undefined
  const id = body.storage_integration_id
  return typeof id === "string" ? id : undefined
}

function markStorageIntegrationInvalid(queryClient: QueryClient, integrationId?: string) {
  queryClient.setQueriesData<StorageIntegration[]>({ queryKey: storageIntegrationsQueryKeys.lists() }, (integrations) =>
    integrations?.map((integration) =>
      !integrationId || integration.id === integrationId ? { ...integration, auth_status: "invalid" } : integration
    )
  )
}

type ErrorToastMeta =
  | false
  | string
  | ((error: Error) => string | null | undefined)
  | { fallback: string }
  | { message: (error: Error) => string | undefined; fallback: string }

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      errorToast?: ErrorToastMeta
    }
  }
}

function getErrorMessage(error: Error, errorToast: ErrorToastMeta | undefined): string | null {
  // Explicitly disabled
  if (errorToast === false) return null

  // Custom static string
  if (typeof errorToast === "string") return errorToast

  // Custom function (no fallback specified - use global)
  if (typeof errorToast === "function") {
    const result = errorToast(error)
    return result === null ? null : result || GLOBAL_ERROR_FALLBACK
  }

  // Object form with fallback
  if (typeof errorToast === "object") {
    const message = "message" in errorToast ? errorToast.message(error) : error.message
    return message || errorToast.fallback
  }

  // Default: error.message with global fallback
  return error.message || GLOBAL_ERROR_FALLBACK
}

const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (handleInactivityLogout(error)) return

    const message = getErrorMessage(error, mutation.meta?.errorToast)
    if (message) {
      console.error(error)
      Sentry.captureException(error, {
        level: "warning",
        extra: error instanceof DirectUploadError ? { ...error.details } : undefined,
      })
      toast.error(message)
    }
  },
})

interface ReactQueryProviderProps {
  persistUserCache?: boolean
  children: ReactNode
}

function transformDates(obj: unknown): unknown {
  if (obj === null || typeof obj !== "object") return obj

  if (Array.isArray(obj)) {
    return obj.map(transformDates)
  }

  const result = { ...obj } as Record<string, unknown>
  for (const key in result) {
    const value = result[key]
    if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:?\d{2})$/.test(value)
    ) {
      result[key] = new Date(value)
    } else if (typeof value === "object") {
      result[key] = transformDates(value)
    }
  }
  return result
}

export default function ReactQueryProvider({ children, persistUserCache = true }: ReactQueryProviderProps) {
  const { sessionId } = useAuth()
  const [queryClient] = useState(() => {
    const client: QueryClient = new QueryClient({
      mutationCache,
      queryCache: new QueryCache({
        onError: (error) => {
          if (handleInactivityLogout(error)) return

          // Matched by the unique error code (not status) so it stays correct regardless
          // of which 4xx the backend uses for "this storage integration needs reauth".
          if (error instanceof ApiRequestError && error.message === STORAGE_INVALID_AUTH_ERROR) {
            markStorageIntegrationInvalid(client, invalidStorageIntegrationId(error))
          }
        },
      }),
      defaultOptions: {
        queries: {
          staleTime: 60 * 1000,
          select: (data) => transformDates(data),
        },
      },
    })
    return client
  })

  useEffect(() => {
    // Anonymous forms do not use the advisor cache, and third-party embeds may
    // deny storage access entirely.
    if (!persistUserCache) return

    const persister = createSyncStoragePersister({
      storage: window.localStorage,
      key: "slant-query-cache",
    })

    const [unsubscribe] = persistQueryClient({
      queryClient,
      persister,
      buster: `user-capabilities-v3:${sessionId || "signed-out"}`,
      maxAge: 1000 * 60 * 60,
      dehydrateOptions: {
        shouldDehydrateQuery: ({ queryKey, state }) => {
          return queryKey[0] === "user" && state.status === "success"
        },
      },
    })
    return unsubscribe
  }, [persistUserCache, queryClient, sessionId])

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
